import { useEffect, useRef, useCallback } from "react";

const WS_RECONNECT_DELAY = 3000;
const PING_INTERVAL = 25000;

/**
 * WebSocket hook for real-time notification updates.
 * Connects to /api/ws/notifications with the user's JWT token.
 * Calls onMessage when the server pushes data (e.g. unread_count updates).
 */
export function useNotificationWS(token, onMessage) {
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const mountedRef = useRef(true);

  const connect = useCallback(() => {
    if (!token || !mountedRef.current) return;

    // Build WebSocket URL from the app's backend URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = baseUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Start ping interval to keep connection alive
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") return;
        try {
          const data = JSON.parse(event.data);
          if (onMessage) onMessage(data);
        } catch {}
      };

      ws.onclose = () => {
        clearInterval(pingRef.current);
        if (mountedRef.current) {
          reconnectRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, [token, onMessage]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      clearInterval(pingRef.current);
      clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);
}
